/* eslint-disable react/prop-types */
import React from 'react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { ReportImage } from '@models/DomainModels/Master/report-image';
import { ImageCard } from './ImageCard';
import { SectionHeader } from '../ui/SectionHeader';
import styles from './ImageGrid.module.scss';
import { DynamicStore } from '@state/interfaces/idynamic-store';

interface ImageGridProps {
  sectionKey: 'header' | 'footer';
  sectionLabel: string;
  images: ReportImage[];
  imageData: Record<string, string>;
  loadingImages: Set<string>;
  onAddClick: (section: 'header' | 'footer') => void;
  onLoadImage: (imageId: string) => void;
  onDeleteImage?: (imageId: string) => void;
  onViewFullSize?: (image: ReportImage) => void;
  onDialogSuccess?: (imageId?: string) => void;
  store?: DynamicStore;
  organizationId: string;
}

/**
 * ImageGrid component displays a grid of images for a given section (header or footer).
 *
 * @param props - The props for the ImageGrid component
 * @param props.sectionKey - Section identifier ('header' or 'footer')
 * @param props.sectionLabel - Localized label for the section
 * @param props.images - Array of ReportImage objects to display
 * @param props.imageData - Map of image IDs to their data (e.g., base64 strings)
 * @param props.loadingImages - Set of image IDs currently loading
 * @param props.onAddClick - Handler for add button click
 * @param props.onLoadImage - Handler for loading an image
 * @param props.onDeleteImage - Handler for deleting an image
 * @param props.onViewFullSize - Handler for viewing an image in full size
 * @param props.store - The Redux store
 * @param props.organizationId - The organization ID
 * @returns The rendered ImageGrid component
 */
export const ImageGrid: React.FC<Readonly<ImageGridProps>> = React.memo(
  ({ sectionKey, sectionLabel, images, imageData, loadingImages, onAddClick, onLoadImage, onDeleteImage, onViewFullSize, onDialogSuccess, store, organizationId }) => {
    return (
      <Box className={styles.section}>
        <SectionHeader sectionKey={sectionKey} sectionLabel={sectionLabel} onAddClick={onAddClick} />

        <Grid container spacing={2} className={styles.cardGrid}>
          {images.map(image => (
            <ImageCard
              key={image.id}
              image={image}
              imageData={imageData[image.id]}
              isLoading={loadingImages.has(image.id)}
              onLoadImage={onLoadImage}
              section={sectionKey}
              organizationId={organizationId}
              store={store}
              onDelete={onDeleteImage}
              onViewFullSize={onViewFullSize}
              onDialogSuccess={onDialogSuccess}
            />
          ))}
        </Grid>
      </Box>
    );
  }
);

ImageGrid.displayName = 'ImageGrid';
